import { SimplePlayer, PlayerStatus } from './simple-player';
import { StatusStorage } from './status-storage';
import { clientWidth, clientHeight, os, offsetRight, astrict, debounce, isInLeft} from "./utils";
import { Config } from './host'
import { AccessModel, IALectureInfo } from './helper'
import { getLectureAdvance } from './services/lecture'

interface AudioSource { id: number, url: string, img: string, ref: string }

console.log('230313部署集群===');

// global.d.ts
declare global {
    interface Window {
        Session?: {
            getToken: (fb?: (data: {token: string}) => void) => void;
        }
    }
}

const checkCachePromise: {
    [lectureId: string]: Promise<boolean>
} = {}

/**
 * 播放的课程信息
 */
interface IPlayLectureBaseInfo {
  channel_id: number,
  liveroom_id: number,
  // 当前播放的课程
  lecture_id: number
  // 下一节课程
  next_lecture_id?: number | null
}

/**
 * 检查是否有听课权限
 * @param lectureId
 */
const checkLectureCanListen = (lectureId: number | string, baseUrl?: string) => {
    const cacheValue = checkCachePromise[lectureId]
    if (cacheValue) {
        return cacheValue
    }
    const result = new Promise<boolean>((resolve, reject) => {
        if (window.location.pathname === `/lecture2/${lectureId}`) {
            // 如果在课程详情页停留，默认就不校验权限了。能停留在课程详情页学习，一般是有权限的。
            resolve(true)
            return
        }
        if (!window.Session) {
            resolve(true)
            return
        }
        window.Session.getToken((sessionData) => {
            const xhr = new XMLHttpRequest()
            const token = sessionData.token
          xhr.open('GET', `${baseUrl||Config.BASE_URL}/api/lecture/${lectureId}/info?token=${token}`, true)
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        const res = JSON.parse(xhr.responseText)
                        if (res.code !== 0) {
                            console.error('checkLectureCanListen', res)
                            reject(res)
                            return
                        }
                        resolve(AccessModel.getLectureInfoAccess(res.data as IALectureInfo).canListen)
                    } else {
                        reject(xhr.responseText)
                    }
                }
            }
            xhr.send()
        })
    })
    checkCachePromise[lectureId] = result
    return result
}

export interface GlobalPlayerStatus {
    position: { right: number, bottom: number }
    zIndex: number
    audio: null | AudioSource
    play: boolean
    currentTime: number
    current_lecture: null | IPlayLectureBaseInfo
}

export interface GlobalPlayerPlaylist {
    content: AudioSource[]
}

/**
 * 全局音频播放器
 *
 * 播放逻辑说明:
 *
 * 1. 旧版逻辑
 * - 在课程详情页播放时,会将当前课程及所属专栏的所有音频课程信息(包含播放链接)一次性存入 localStorage
 * - 存在安全风险,容易被批量获取播放链接
 *
 * 2. 新版逻辑
 * - 只传入当前要播放的课程 ID
 * - 播放时按需获取当前课程的播放信息
 * - 播放完成后再获取下一课程信息
 * - 优势:安全性更高,减少接口压力
 */
export class GlobalPlayer {
    public baseUrl = '' // 可在外部修改
    private static _instance: GlobalPlayer | null = null
    readonly player = new SimplePlayer(false)
    private btnW = 54
    private btnH = 54
    private circlePrgR = 22
    private circlePrgX = this.btnW / 2
    private circlePrgY = this.btnH / 2
    private readonly status: StatusStorage<GlobalPlayerStatus>
    private readonly playlist: StatusStorage<GlobalPlayerPlaylist>
    private el: HTMLDivElement | null = null

    private timeUpdateHandler: ((status: GlobalPlayerStatus) => any) | null = null

    constructor(storageKey: string,
                defaultStatus: GlobalPlayerStatus, storage: Storage = window.sessionStorage || window.localStorage) {
        if (GlobalPlayer._instance) {
            throw new Error('全局播放器只能初始化一次')
        }
        GlobalPlayer._instance = this

        this.status = new StatusStorage<GlobalPlayerStatus>(storageKey + '/Status', defaultStatus, storage)
        this.playlist = new StatusStorage<GlobalPlayerPlaylist>(storageKey + '/Playlist', { content: [] }, storage)
        this.player.on('timeUpdate', (currentTime: number) => {
            if (this.player.getStatus().status === 'playing' && currentTime >= 1) {
                this.status.set('currentTime', currentTime)
                typeof this.timeUpdateHandler === "function" && this.timeUpdateHandler(this.getStatus())
            }
        })

        this.player.on('ended', () => {
            this.nextAudio()
            this.setCurrentTime(0)
        })
    }

    private async nextAudio() {
      const { current_lecture } = this.status.getAll()
      if (current_lecture) {
        this.nextAudioV2()
      } else {
        this.nextAudioV1()
      }
    }
    private async nextAudioV2() {
      const { current_lecture } = this.status.getAll()
      if (!current_lecture || !current_lecture.next_lecture_id) {
        // 没有下一节课了。
        return
      }
      this.status.set('current_lecture', {
        ...current_lecture,
        lecture_id: current_lecture.next_lecture_id,
        next_lecture_id: null
      })
      await this.initCurrentLectureAudioInfo()
      const {audio} = this.status.getAll()
      if (audio) {
        this.player.load(audio.url)
        const imgEl = document.getElementById('wk-global-player-cover-img')
        const btnContent = document.getElementById('wk-global-player-return-lecture-a') as HTMLAnchorElement
        if (imgEl && btnContent) {
          imgEl.style.backgroundImage = `url(${audio.img})`
          btnContent.href = audio.ref
        }
        this.player.play()
      }
    }
    private async nextAudioV1() {
        const playlist = this.playlist.get('content')
        let next: AudioSource | null = null
        const current = this.status.get('audio')

        if (!current) {
            next = playlist[0] || null
        } else {
            for (let i = 0; i < playlist.length; i++) {
                const audio = playlist[i];
                if (audio.id === current.id) {
                    next = playlist[i + 1]  || 0
                    continue
                }
            }
        }

        if (next) {
            try {
                const isCanListen =  await checkLectureCanListen(next.id, this.baseUrl)
                if (!isCanListen) {
                    this.clearAudio()
                    this.hide()
                    return
                }
            } catch (e) {
                console.error('checkLectureCanListen', e)
            }
            this.setAudio(next, this.playlist.get('content'))
            this.player.load(next.url)
            const imgEl = document.getElementById('wk-global-player-cover-img')
            const btnContent = document.getElementById('wk-global-player-return-lecture-a') as HTMLAnchorElement
            if (imgEl && btnContent) {
                imgEl.style.backgroundImage = `url(${next.img})`
                btnContent.href = next.ref
            }
            this.player.play()
        }
    }

    onTimeUpdate(func: GlobalPlayer['timeUpdateHandler']) {
        this.timeUpdateHandler = func
    }

    getStatus() {
        return this.status.getAll()
    }
    /**
     * 设置要播放的音频信息
     * @deprecated 后续不要再使用了，请使用 setPlayLecture。 目前保留是为了兼容已存储在本地的数据。
     * @param audio
     * @param playlist
     */
    setAudio(audio: GlobalPlayerStatus['audio'], playlist?: GlobalPlayerPlaylist['content']) {
        this.status.set('audio', audio)
        this.playlist.set('content', playlist || [])
        this.setCurrentTime(0)
    }

    /**
     * 设置播放的课程
     */
    public setPlayLecture(lectureInfo: GlobalPlayerStatus['current_lecture']) {
      this.status.set('audio', null)
      this.playlist.set('content', [])
      this.status.set('current_lecture', lectureInfo)
      this.setCurrentTime(0)
    }

    public clearAudio() {
        this.setAudio(null)
        this.setPlayLecture(null)
    }

    setCurrentTime(currentTime: GlobalPlayerStatus['currentTime']) {
        this.status.set('currentTime', currentTime)
    }

    setPlay(b: boolean) {
        this.status.set('play', b)
    }

    setPosition(pos: {right: number, bottom: number}) {
        this.status.set('position',{
            right: pos.right - offsetRight(),
            bottom: pos.bottom
        })

        if(this.el) {
            this.el.style.right = `${pos.right - offsetRight()}px`
            this.el.style.bottom = `${pos.bottom}px`
        }
    }

    public async play() {
      const { current_lecture } = this.status.getAll()
      if (current_lecture) {
        await this.initCurrentLectureAudioInfo()
      }
      this.playInner()
    }
    private async playInner() {
        const { audio, current_lecture } = this.status.getAll()
        if (audio) {
          if (!current_lecture) {
            try {
              const isCanListen =  await checkLectureCanListen(audio.id, this.baseUrl)
              if (!isCanListen) {
                this.clearAudio()
                this.hide()
                return
              }
            } catch (e) {
              console.error('checkLectureCanListen', e)
            }
          }
          this.status.set('play', true)
          return this.player.play()
        }
    }

    pause() {
        const { audio } = this.status.getAll()
        if (audio) {
            this.status.set('play', false)
            return this.player.pause()
        }
    }

    public toggle = debounce(() => {
        const { status } = this.player.getStatus()
        const lectureEl = document.getElementById('wk-global-player-return-lecture')
        if (lectureEl) {
            if (status !== "pausing") {
                this.pause()

                lectureEl.style.opacity = '1'
                lectureEl.style.width = '181px'

                const { position } = this.getStatus()
                if (isInLeft(position.right)) {
                    lectureEl.className = 'wk-return-lecture-left'
                } else {
                    lectureEl.className = 'wk-return-lecture-right'
                }

                //3s后自动隐藏
                this.timer = window.setTimeout(() => {
                    lectureEl.style.opacity = '0'
                    lectureEl.style.width = '0px'
                }, 3000)
            } else {
                this.play()

                lectureEl.style.opacity = '0'
                lectureEl.style.width = '20px'
                clearTimeout(this.timer)
            }
        }
    }, 100)

    /**
     * 初始化当前播放课程的信息
     * 
     * 该方法主要做以下几件事:
     * 1. 获取当前课程的播放信息(播放地址、封面等)
     * 2. 获取下一节课程ID 
     * 3. 更新播放状态
     * 
     * @private
     */
    private async initCurrentLectureAudioInfo() {
      const { current_lecture } = this.status.getAll()
      if (current_lecture) {
        // 获取课程的播放信息
        const lectureAdvance = await getLectureAdvance({
          channel_id: current_lecture.channel_id,
          liveroom_id: current_lecture.liveroom_id,
          lecture_id: current_lecture.lecture_id,
          lecture_mode: 'audio'
        }, true)

        // 更新当前课程信息,主要是设置下一节课程ID
        this.status.set('current_lecture', {
          ...current_lecture,
          next_lecture_id: lectureAdvance.next_lecture_id
        })

        // 设置音频信息,包含播放地址、封面图等
        this.status.set('audio', {
          id: current_lecture.lecture_id,
          url: lectureAdvance.advance_info.url,
          img: lectureAdvance.cover_url,
          ref: `/lecture2/${current_lecture.lecture_id}`
        })
      }
    }

    public async show() {
      // app里初始化全局播放会导致app音视频播放音频变小
      const isApp = /lycheer/.test(navigator.userAgent.toLocaleLowerCase())
      if (isApp) {
        return
      }
      const { current_lecture } = this.status.getAll()
      if (current_lecture) {
        await this.initCurrentLectureAudioInfo()
      }
      this.showInner()
    }
    private async showInner() {
        //app里初始化全局播放会导致app音视频播放音频变小
        const isApp = /lycheer/.test(navigator.userAgent.toLocaleLowerCase())
        if(isApp) return

        if (!this.el) {
            this.player.init()
            this.render()
        }

        const { audio, play, currentTime, current_lecture } = this.status.getAll()
        const el = this.el
        const imgEl = document.getElementById('wk-global-player-cover-img')
        const btnContent =  document.getElementById('wk-global-player-return-lecture-a') as HTMLAnchorElement

        if (audio && el && imgEl && btnContent) {
            if (!current_lecture) {
              try {
                const isCanListen =  await checkLectureCanListen(audio.id, this.baseUrl)
                if (!isCanListen) {
                  this.clearAudio()
                  this.hide()
                  return
                }
              } catch (e) {
                console.error('checkLectureCanListen', e)
              }
            }
            this.player.load(audio.url)
            imgEl.style.backgroundImage = `url(${audio.img})`
            btnContent.href = audio.ref

            if (currentTime) {
                this.player.seek(currentTime)
            }

            if (play) {
                this.player.play()
            }

            el.style.display = 'block'
        }
    }

    hide() {
        if (this.el) {
            this.player.pause()
            this.el.style.display = 'none'
        }
    }

    // 创建 dom
    private inMove: boolean = false
    private timer:number = 0
    private bindMove() {
        if (this.el) {
            const el = this.el

            let right = 0
            let bottom = 0
            let x0 = 0
            let y0 = 0

            const body = document.querySelector('body')

            const handleMouseDown = (_evt: MouseEvent | TouchEvent) => {
                const evt = _evt as any
                x0 = evt.x || (evt.x === 0 ? 0 : evt.touches[0].clientX)
                y0 = evt.y || (evt.y === 0 ? 0 : evt.touches[0].clientY)

                right = parseInt(el.style.right as string)
                bottom = parseInt(el.style.bottom as string)

                document.addEventListener('mousemove', handleMouseMove)
                document.addEventListener('mouseup', handleMouseUp)

                document.addEventListener('touchmove', handleMouseMove,{ passive: false })
                document.addEventListener('touchend', handleMouseUp)

                if(body){
                    body.style.overflow = 'hidden'
                    body.style.touchAction = 'none'

                    os.isIOS && _evt.preventDefault()
                }
            }

            const handleMouseMove = (_evt: MouseEvent | TouchEvent) => {

                const evt = _evt as any
                const dx = (evt.x || (evt.x === 0 ? 0 : evt.touches[0].clientX)) - x0
                const dy = (evt.y || (evt.y === 0 ? 0 : evt.touches[0].clientY)) - y0

                this.inMove = this.inMove || (Math.abs(dx) + Math.abs(dy) > 15)

                const newRight = astrict(Math.min(Math.max(right - dx, 0), clientWidth() - this.btnW))
                const newBottom = Math.min(Math.max(bottom - dy, 0), clientHeight() - this.btnH)

                el.style.right = `${newRight}px`
                el.style.bottom = `${newBottom}px`

                if(body){
                    body.style.overflow = 'hidden'
                    body.style.touchAction = 'none'

                    _evt.preventDefault()
                }
            }

            const handleMouseUp = () => {
                setTimeout(() => this.inMove = false, 30)
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)

                document.removeEventListener('touchmove', handleMouseMove)
                document.removeEventListener('touchend', handleMouseUp)

                this.status.set('position', {
                    right: parseInt(el.style.right as string) - offsetRight(),
                    bottom: parseInt(el.style.bottom as string)
                })

                if(body){
                    body.style.overflow = 'auto'
                    body.style.touchAction = 'auto'
                }

                if (!this.inMove) {
                    this.toggle()
                }
            }

            el.addEventListener('mousedown', handleMouseDown)
            el.addEventListener('touchstart', handleMouseDown,{ passive: false })
        }
    }

    private bindEvent() {
        const progressEl = document.getElementById('wk-global-player-progress')
        const coverEl = document.getElementById('wk-global-player-cover')
        const coverImg = document.getElementById('wk-global-player-cover-img')
        if (progressEl) {
            this.player.on('timeUpdate', (t, d) => {
                const audioPercent = d!==0 ? Math.ceil(t * Math.PI * this.circlePrgR * 2 / d) : 0
                progressEl.innerHTML = `<circle
                  stroke-dasharray="${audioPercent},300"
                  cx="${this.circlePrgX}"
                  cy="${this.circlePrgY}"
                  r="${this.circlePrgR}"
                  stroke-width="2px"
                  stroke="#FF5232"
                  >
                </circle>`
            })
        }

        const statusEl = document.getElementById('wk-global-player-status')
        const lectureEl = document.getElementById('wk-global-player-return-lecture')
        const lectureaEl = document.getElementById('wk-global-player-return-lecture-a') as HTMLAnchorElement
        const loadingImg = 'https://static-1253442168.file.myqcloud.com/xupload/20190618_loading.png'
        const pausingImg = 'https://static-1253442168.file.myqcloud.com/xupload/20190618_pausing.png'
        const playingImg = 'https://static-1253442168.file.myqcloud.com/xupload/20190618_play.png'
        if (statusEl) {
            this.player.on('status', (status: PlayerStatus) => {
                statusEl.innerHTML = `<img id="${status === 'loading' && 'wk-global-player-loading'}" src="${status === 'loading' ? loadingImg : status === 'playing' ? pausingImg : playingImg}" alt="" />`
                if (coverEl && coverImg && lectureEl) {
                    if (status === 'pausing') {
                        const iTransform = getComputedStyle(coverImg).transform
                        const cTransform = getComputedStyle(coverEl).transform
                        if(iTransform && cTransform){
                            coverEl.style.transform = cTransform === 'none'
                                ? iTransform
                                : iTransform.concat(' ', cTransform)
                        }
                        coverImg.classList.add('wk-global-player-cover-pause')
                        coverImg.classList.remove('wk-global-player-cover-play')
                    } else {
                        coverImg.classList.add('wk-global-player-cover-play')
                        coverImg.classList.remove('wk-global-player-cover-pause')
                        // 蛋疼埋点逻辑..
                        try {
                            const lec_id = lectureaEl.href.split('/').pop()
                            if((window as any).TorDataService && lec_id) {
                                (window as any).TorDataService.emitAction('le_play_ck', { lec_id });
                            }
                        } catch(e) {
                            console.log(e)
                        }
                    }
                }
            })

        }

        this.bindMove()

        if(lectureaEl && os.isIOS){//修复ios a标签active不生效
            lectureaEl.addEventListener('touchstart', (e) => {
                lectureaEl.style.background = '#E4431B'
            })
            lectureaEl.addEventListener('touchend', (e) => {
                lectureaEl.style.background = '#FF5A31'
                location.assign(lectureaEl.href)
            })
        }

        const cancelGlobalPlayer = document.getElementById('wk-global-player-cancel')
        if(cancelGlobalPlayer){
            cancelGlobalPlayer.addEventListener('mousedown', (e) => {
                this.clearAudio()
                this.hide()
            })
            cancelGlobalPlayer.addEventListener('touchend', (e) => {
                this.clearAudio()
                this.hide()
            })
        }
    }

    private render() {
        if (this.el) { return }
        const style = document.createElement('style')
        const { img } = this.status.get('audio') || { img: '' }
        const { right, bottom } = this.status.get('position') || { right: 10, bottom: 10 }
        const zIndex = this.status.get('zIndex')

        style.innerHTML = `
        #wk-global-player {
            position: fixed;
            z-index: ${zIndex};
        }
        #wk-global-player-bg{
            width: ${this.btnW}px;
            height:${this.btnH}px;
            padding:5px;
            background:#fff;
            border-radius:100%;
            box-shadow: 0 3px 17px -11px #333;
            box-sizing: border-box;
            position: relative;
            z-index: 1;

            -webkit-user-select: none;
            user-select: none;
        }
        #wk-global-player-cover {
            position:relative;
            z-index:1;
        }
        #wk-global-player-cover-img{
            width: ${this.btnW - 10}px;
            height: ${this.btnH - 10}px;
            border-radius:100%;
            overflow:hidden;
            background-size:cover;
            background-position: center;
            background-repeat: no-repeat;
        }
        .wk-global-player-cover-play{
            animation: rotation 30s infinite linear;
            animation-fill-mode: forwards;
            -webkit-animation: rotation 30s infinite linear;
            -webkit-animation-fill-mode: forwards;
            overflow:hidden;
        }

        .wk-global-player-cover-pause{
            animation-play-state: paused!important;
            -webkit-animation-play-state:paused!important;
        }

        #wk-global-player-cover img {
            width: 100%;
            height: 100%;

            user-drag: none;
            -webkit-user-drag: none;
        }

        #wk-global-player-status{
            width: ${this.btnW - 10}px;
            height: ${this.btnH - 10}px;
            position: absolute;
            top:50%;
            left:50%;
            transform: translate(-50%,-50%);
            -webkit-transform: translate(-50%,-50%);
            border-radius:100%;
            overflow:hidden;
            z-index:2;
            background-color:rgba(0,0,0,0.3);
            display: flex;
            display: -webkit-flex;
            justify-content: center;
            align-items: center;
            -webkit-justify-content: center;
            -webkit-align-items: center;
            cursor: pointer;

        }

        #wk-global-player-status img{
            width:44px;
            height:44px;
            cursor: pointer;

            user-drag: none;
            -webkit-user-drag: none;
        }

        #wk-global-player-progress{
            width: ${this.btnW}px;
            height: ${this.btnW}px;
            position: absolute;
            left: 0;
            top: 0;
            z-index:1;
        }
        #wk-global-player-progress circle{
            fill: none;
            stroke-linecap: round;
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: 0.5s stroke-dasharray;
          }
          
        #wk-global-player-return-lecture{
            height:42px;
            padding:5px;
            position: absolute;
            right:11px;
            top:6px;
            box-shadow: 0 3px 8px 0 rgba(0,0,0,0.10);
            border-radius: 27px;
            box-sizing: border-box;
            background: #FFFFFF;
            z-index: 0;
            transition: 0.3s all;
            overflow:hidden;
        }
        #wk-global-player-return-lecture a{
            display:block;
            width:92px;
            background: #FF5A31;
            border-radius: 22px;
            color:#fff;
            font-size:14px;
            box-sizing: border-box;
            text-decoration:none;
            line-height:32px;
            text-align:center;
            cursor: pointer;
        }
        #wk-global-player-return-lecture a:active{
            color:#fff;
            background: #E4431B;
        }
        #wk-global-player-loading{
            animation: playLoadingMove 1s infinite linear;
            animation-fill-mode: forwards;
            -webkit-animation: playLoadingMove 1s infinite linear;
            -webkit-animation-fill-mode: forwards;
        }

        #wk-global-player-return-lecture.wk-return-lecture-left{
            right: unset;
            left: 11px;
        }
        #wk-global-player-return-lecture.wk-return-lecture-left a{
            margin-left:43px;
            float:left;
        }
        #wk-global-player-return-lecture.wk-return-lecture-left span{
            margin-left:10px;
            float:left;
        }
        #wk-global-player-return-lecture.wk-return-lecture-right a{
            float:right;
            margin-right:43px;
        }
        #wk-global-player-return-lecture.wk-return-lecture-right span{
            margin-right:10px;
            float:right;
        }
        
        #wk-global-player-cancel {
            display:block;
            padding:6px 0;
            width:20px;
            cursor: pointer;
            font-size:0;
        }
        #wk-global-player-cancel img{
            width:100%;
        }

        @keyframes rotation {
            100%{
               transform:rotate(1turn);
               -webkit-transform:rotate(1turn);
            }
        }
        @-webkit-keyframes rotation {
            100%{
               transform:rotate(1turn);
               -webkit-transform:rotate(1turn);
            }
        }
        @keyframes playLoadingMove {
            0%{
              transform: rotate(0);
            }
            50%{
              transform: rotate(180deg);
            }
            100%{
              transform: rotate(360deg);
            }
        }
        -webkit-@keyframes playLoadingMove {
            0%{
              transform: rotate(0);
            }
            50%{
              transform: rotate(180deg);
            }
            100%{
              transform: rotate(360deg);
            }
        }
        `

        this.el = document.createElement('div')
        this.el.id = 'wk-global-player'

        this.el.style.right = `${right + offsetRight()}px`
        this.el.style.bottom = `${bottom}px`
        this.el.style.display = 'none'

        this.el.innerHTML = `
            <div id="wk-global-player-bg">
                <div id="wk-global-player-status">
                    <img src="https://static-1253442168.file.myqcloud.com/xupload/20190618_play.png" alt="">
                </div>
                <svg id="wk-global-player-progress" version="1.1" xmlns="http://www.w3.org/2000/svg"></svg>
                <div id="wk-global-player-cover">
                    <div id="wk-global-player-cover-img" class="wk-global-player-cover-pause" style="background-image: url(${img})"></div>
                </div>
            </div>
            <div id="wk-global-player-return-lecture" style="opacity:0;width: 20px">
                <a id="wk-global-player-return-lecture-a" href="https://m.lizhiweike.com">返回课程</a>
                <span id="wk-global-player-cancel"><img src="https://static-1253442168.file.myqcloud.com/xupload/20190621_ico-cancel.png" alt=""></span>
            </div>
        `

        document.head.appendChild(style)
        document.body.appendChild(this.el)
        this.bindEvent()
    }
}
